// Engine entry point — factory that picks WebGPU or WebGL2 backend.
import { WebGL2Engine } from './fieldEngine.js';

let _gpuEngine = null;
let _gpuFailed = false;

export async function createEngine(canvas, count = 30000) {
  // Check for force flags
  const params = new URLSearchParams(location.search);
  const forceGL = params.has('gl');
  const forceGPU = params.has('webgpu');

  function makeGL2() {
    const e = new WebGL2Engine(canvas, count);
    if (!e.init(count)) {
      console.warn('[engine] WebGL2 init failed');
      return null;
    }
    return e;
  }

  if (forceGL) {
    console.log('[engine] ?gl flag — forcing WebGL2');
    return makeGL2();
  }

  // Try WebGPU
  if (!forceGPU && _gpuFailed) {
    console.log('[engine] WebGPU previously failed, using WebGL2');
    return makeGL2();
  }

  // Quick GPU capability check
  let hasRealGPU = false;
  try {
    const testCanvas = document.createElement('canvas');
    const gl = testCanvas.getContext('webgl2');
    if (gl) {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      if (dbg) {
        const r = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
        if (r && r !== '' && !r.toLowerCase().includes('swiftshader') && !r.toLowerCase().includes('llvmpipe') && !r.toLowerCase().includes('software')) {
          hasRealGPU = true;
        }
      }
    }
  } catch (e) { /* ignore */ }

  if (!hasRealGPU && !forceGPU) {
    console.log('[engine] Software renderer detected, using WebGL2');
    return makeGL2();
  }

  // Try WebGPU with watchdog
  if (navigator.gpu) {
    // Lazy-load GPU engine
    try {
      const mod = await import('./gpuEngine.js');
      const engine = new mod.WebGPUEngine(canvas, count);
      const ok = await Promise.race([
        engine.init(count),
        new Promise(r => setTimeout(() => r(false), 2500))
      ]);
      if (ok) {
        console.log('[engine] WebGPU backend active');
        _gpuEngine = engine;
        return engine;
      }
      engine.destroy();
    } catch (e) {
      console.warn('[engine] WebGPU init failed:', e.message);
    }
  }

  _gpuFailed = true;
  console.log('[engine] Falling back to WebGL2');
  return makeGL2();
}
